import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FirebaseAuthContext } from 'context/FirebaseAuthContext.js';
import PageHeader from 'components/common/PageHeader';
import { Card, Tab, Tabs, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRocket,
  faCheckCircle,
  faTimesCircle,
  faCog
} from '@fortawesome/free-solid-svg-icons';

import { db } from 'config/firebase.js';
import { collection, query, where, onSnapshot } from 'firebase/firestore';

import CatalogMatchGrid from './CatalogMatchGrid';
import VerifiedTab from './tabs/VerifiedTab';
import NoMatchTab from './tabs/NoMatchTab';
import DiscoverySettingsTab from './tabs/DiscoverySettingsTab';

const VERIFICATIONS_PATH = 'productCatalog/topicz/asinVerifications';

/**
 * AmazonMatchingHub - Unified interface for Amazon product matching.
 * 
 * Tabs:
 * - Opportunity Board: Products with discovered matches ready for activation
 * - Verified: Confirmed matches ready for Amazon selling
 * - No Match: Products without ASIN matches (can retry)
 * - Settings: Discovery configuration
 */
const AmazonMatchingHub = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, authLoading } = useContext(FirebaseAuthContext);

  // Tab state from URL or default
  const activeTab = searchParams.get('tab') || 'opportunities';

  // Stats for tab badges
  const [stats, setStats] = useState({
    opportunities: 0,
    verified: 0,
    noMatch: 0,
    loading: true
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (authLoading) return;
    if (!user) return navigate('/');
  }, [authLoading, user, navigate]);

  // Real-time stats listener
  useEffect(() => {
    // Opportunities: pending_review, low_confidence, auto_matched (not yet confirmed)
    const opportunitiesQuery = query(
      collection(db, VERIFICATIONS_PATH),
      where('verificationStatus', 'in', ['pending_review', 'low_confidence', 'auto_matched'])
    );

    // Verified
    const verifiedQuery = query(
      collection(db, VERIFICATIONS_PATH),
      where('verificationStatus', '==', 'verified')
    );

    // No match
    const noMatchQuery = query(
      collection(db, VERIFICATIONS_PATH),
      where('verificationStatus', '==', 'not_found')
    );

    const unsubscribeOpportunities = onSnapshot(opportunitiesQuery, (snapshot) => {
      setStats(prev => ({ ...prev, opportunities: snapshot.size, loading: false }));
    });

    const unsubscribeVerified = onSnapshot(verifiedQuery, (snapshot) => {
      setStats(prev => ({ ...prev, verified: snapshot.size }));
    });

    const unsubscribeNoMatch = onSnapshot(noMatchQuery, (snapshot) => {
      setStats(prev => ({ ...prev, noMatch: snapshot.size }));
    });

    return () => {
      unsubscribeOpportunities();
      unsubscribeVerified();
      unsubscribeNoMatch();
    };
  }, []);

  // Tab change handler - updates URL
  const handleTabChange = (tab) => {
    setSearchParams({ tab });
  };

  if (authLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      <PageHeader title="Amazon Product Matching" className="mb-3">
        <p className="mb-0 fs--1 text-600">
          Discover Amazon matches for your products and activate them for selling
        </p>
      </PageHeader>

      <Card>
        <Card.Header className="p-0">
          <Tabs
            activeKey={activeTab}
            onSelect={handleTabChange}
            className="nav-tabs-card"
          >
            <Tab
              eventKey="opportunities"
              title={
                <span>
                  <FontAwesomeIcon icon={faRocket} className="me-2" />
                  Smart Dashboard
                  {stats.opportunities > 0 && (
                    <Badge bg="warning" className="ms-2">
                      {stats.opportunities}
                    </Badge>
                  )}
                </span>
              }
            />
            <Tab
              eventKey="verified"
              title={
                <span>
                  <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                  Verified
                  {stats.verified > 0 && (
                    <Badge bg="success" className="ms-2">
                      {stats.verified}
                    </Badge>
                  )}
                </span>
              }
            />
            <Tab
              eventKey="no-match"
              title={
                <span>
                  <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                  No Match
                  {stats.noMatch > 0 && (
                    <Badge bg="secondary" className="ms-2">
                      {stats.noMatch}
                    </Badge>
                  )}
                </span>
              }
            />
            <Tab
              eventKey="settings"
              title={
                <span>
                  <FontAwesomeIcon icon={faCog} className="me-2" />
                  Discovery Settings
                </span>
              }
            />
          </Tabs>
        </Card.Header>

        <Card.Body className="p-0">
          {activeTab === 'opportunities' && <CatalogMatchGrid />}
          {activeTab === 'verified' && <VerifiedTab />}
          {activeTab === 'no-match' && <NoMatchTab />}
          {activeTab === 'settings' && <DiscoverySettingsTab />}
        </Card.Body>
      </Card>
    </>
  );
};

export default AmazonMatchingHub;

